import React, { Component } from 'react';
import { observable, action, autorun } from 'mobx';
import { observer, inject } from 'mobx-react';
import { toast } from 'react-toastify';
import {
  Modal, ModalHeader, ModalBody,
  FormTextarea, Button, Row, Col
} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import moment from 'moment';

const TYPES = [
  { key: 'noPaied', label: '미결제 알림', icon: 'alert-circle' },
  { key: 'noPeriod', label: '기간 만료 알림', icon: 'clock' },
  { key: 'event', label: '이벤트 알림', icon: 'gift' },
  { key: 'planUpgrade', label: '업그레이드 알림', icon: 'arrow-up-circle' },
  { key: 'planDowngrade', label: '다운그레이드 알림', icon: 'arrow-down-circle' },
];

const PLACEHOLDERS = [
  { key: 'storeName', desc: '스토어명' },
  { key: 'name', desc: '담당자명' },
  { key: 'expiryDate', desc: '만료일' },
  { key: 'orderDate', desc: '결제일' },
  { key: 'newPlan', desc: '새 플랜명' },
  { key: 'prevPlan', desc: '이전 플랜명' },
  { key: 'eventTitle', desc: '이벤트 제목' },
];

@inject(({ AOA }) => ({
  getNotificationTypeTemplates: AOA.getNotificationTypeTemplates,
  saveNotificationTypeTemplate: AOA.saveNotificationTypeTemplate,
  previewNotificationTypeTemplate: AOA.previewNotificationTypeTemplate,
}))
@observer
class SmsTemplateModal extends Component {

  @observable activeType = 'noPaied';
  @observable items = [];
  @observable isLoading = false;
  @observable isSaving = false;
  @observable drafts = {};   // {type: {smsMessage}}
  @observable preview = { smsMessage: '' };
  @observable isPreviewLoading = false;
  _previewDebounce = null;

  PERSIST_KEY = 'smsTemplate_drafts';

  componentDidMount() {
    try {
      const saved = window.sessionStorage && window.sessionStorage.getItem(this.PERSIST_KEY);
      if (saved && this.props.isOpen) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.drafts) {
          this.drafts = parsed.drafts;
          if (parsed.activeType) this.activeType = parsed.activeType;
        }
      }
    } catch (e) {}
    this._persistDisposer = autorun(() => {
      try {
        if (this.props.isOpen && Object.keys(this.drafts || {}).length > 0) {
          window.sessionStorage && window.sessionStorage.setItem(this.PERSIST_KEY, JSON.stringify({
            drafts: this.drafts,
            activeType: this.activeType,
          }));
        }
      } catch (e) {}
    });
  }

  componentWillUnmount() {
    if (this._persistDisposer) { try { this._persistDisposer(); } catch (e) {} }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.load();
    }
    if (!this.props.isOpen && prevProps.isOpen) {
      try { window.sessionStorage && window.sessionStorage.removeItem(this.PERSIST_KEY); } catch (e) {}
    }
  }

  @action load = async () => {
    this.isLoading = true;
    try {
      const res = await this.props.getNotificationTypeTemplates();
      if (res && res.data && res.data.error) {
        toast.error(res.data.error);
      } else if (res && res.data && res.data.items) {
        this.items = res.data.items;
        const drafts = {};
        res.data.items.forEach(it => {
          drafts[it.type] = { smsMessage: it.smsMessage || '' };
        });
        this.drafts = drafts;
        this.refreshPreview();
      }
    } catch (e) {
      toast.error('템플릿 조회 실패');
    } finally {
      this.isLoading = false;
    }
  };

  @action setType = (key) => {
    this.activeType = key;
    this.refreshPreview();
  };

  @action onChange = (value) => {
    if (!this.drafts[this.activeType]) this.drafts[this.activeType] = {};
    this.drafts[this.activeType].smsMessage = value;
    this.schedulePreview();
  };

  schedulePreview = () => {
    if (this._previewDebounce) clearTimeout(this._previewDebounce);
    this._previewDebounce = setTimeout(() => this.refreshPreview(), 500);
  };

  @action refreshPreview = async () => {
    const draft = this.drafts[this.activeType];
    if (!draft) return;
    this.isPreviewLoading = true;
    try {
      const res = await this.props.previewNotificationTypeTemplate({
        type: this.activeType,
        smsMessage: draft.smsMessage || '',
      });
      if (res && res.data && !res.data.error) {
        this.preview = { smsMessage: res.data.smsMessage || '' };
      }
    } catch (e) { /* ignore */ }
    finally { this.isPreviewLoading = false; }
  };

  isDirty(type) {
    const item = this.items.find(i => i.type === type);
    const draft = this.drafts[type];
    if (!item || !draft) return false;
    return draft.smsMessage !== (item.smsMessage || '');
  }

  @action save = async () => {
    const draft = this.drafts[this.activeType];
    if (!draft) return;
    this.isSaving = true;
    try {
      const res = await this.props.saveNotificationTypeTemplate({
        type: this.activeType,
        smsMessage: draft.smsMessage || '',
      });
      if (res && res.data && res.data.error) toast.error(res.data.error);
      else { toast.success('문자 템플릿이 저장되었습니다.'); await this.load(); }
    } catch (e) { toast.error('저장 실패'); }
    finally { this.isSaving = false; }
  };

  insertPlaceholder = (ph) => {
    const d = this.drafts[this.activeType] || {};
    const cur = d.smsMessage || '';
    this.onChange(cur + `{${ph}}`);
  };

  close = () => {
    if (this.isSaving) return;
    if (this.props.onClose) this.props.onClose();
  };

  render() {
    const { isOpen } = this.props;
    if (!isOpen) return null;

    const item = this.items.find(i => i.type === this.activeType) || {};
    const draft = this.drafts[this.activeType] || { smsMessage: '' };
    const dirty = this.isDirty(this.activeType);
    const smsLen = (draft.smsMessage || '').length;
    const smsType = smsLen > 90 ? 'LMS' : 'SMS';

    return (
      <>
        <style>{`
          .modal-dialog.sms-tpl-modal { max-width: 900px !important; width: 95vw; }
          .sms-tpl-modal .modal-body { max-height: 85vh; overflow-y: auto; }
        `}</style>
        <Modal size="lg" className="sms-tpl-modal" open={isOpen} toggle={() => {}} centered>
          <ModalHeader>
            <FeatherIcon icon="message-square" size="16" style={{verticalAlign: '-3px', marginRight: 6}}/>
            문자 템플릿 관리
          </ModalHeader>
          <ModalBody>
            <p style={{fontSize: 12, color: '#6B7280', marginBottom: 12}}>
              알림 발송 시 사용되는 SMS 메시지를 타입별로 커스터마이징합니다. placeholder는 발송 시점에 실제 값으로 치환됩니다.
            </p>

            <div style={{display: 'flex', gap: 4, borderBottom: '1px solid #E5E7EB', marginBottom: 14, flexWrap: 'wrap'}}>
              {TYPES.map(t => {
                const active = this.activeType === t.key;
                const itemForT = this.items.find(i => i.type === t.key);
                const tDirty = this.isDirty(t.key);
                const hasSms = itemForT && itemForT.smsMessage && itemForT.hasSaved;
                return (
                  <button key={t.key} type="button" onClick={() => this.setType(t.key)}
                    style={{
                      padding: '10px 14px', fontSize: 13, fontWeight: active ? 700 : 500,
                      color: active ? '#2563EB' : '#6B7280',
                      background: active ? '#EFF6FF' : 'transparent', border: 'none',
                      borderBottom: active ? '2px solid #2563EB' : '2px solid transparent',
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}>
                    <FeatherIcon icon={t.icon} size="13"/>
                    {t.label}
                    {hasSms && <span style={{fontSize: 10, color: '#10B981', marginLeft: 4}}>●</span>}
                    {tDirty && <span style={{fontSize: 10, color: '#D97706', marginLeft: 2}}>*</span>}
                  </button>
                );
              })}
            </div>

            {this.isLoading ? (
              <div className="text-center p-4">로딩 중…</div>
            ) : (
              <>
                <div style={{padding: '8px 12px', background: '#F9FAFB', borderRadius: 6, marginBottom: 10, fontSize: 12, color: '#6B7280'}}>
                  {item.hasSaved
                    ? <>저장됨 · 마지막 수정: {item.updatedAt ? moment(item.updatedAt).format('YYYY-MM-DD HH:mm') : '-'} ({item.updatedBy || '-'})</>
                    : <>이 타입은 아직 커스텀 저장되지 않았습니다. 기본값이 사용됩니다.</>}
                </div>

                <div style={{padding: '8px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, marginBottom: 14, fontSize: 12}}>
                  <strong>변수:</strong>{' '}
                  {PLACEHOLDERS.map(ph => (
                    <code key={ph.key}
                      onClick={() => this.insertPlaceholder(ph.key)}
                      title={`{${ph.key}} 삽입`}
                      style={{background: '#FEF3C7', padding: '1px 6px', borderRadius: 3, marginRight: 6, fontFamily: 'ui-monospace, monospace', cursor: 'pointer'}}>
                      {`{${ph.key}}`} = {ph.desc}
                    </code>
                  ))}
                  <span style={{marginLeft: 6, color: '#6B7280'}}>(클릭하면 끝에 삽입)</span>
                </div>

                <Row>
                  <Col md="7">
                    <label style={{fontSize: 12, fontWeight: 600}}>
                      SMS 메시지
                      <span style={{marginLeft: 8, fontSize: 11, color: '#6B7280'}}>기본값: {item.defaultSms}</span>
                    </label>
                    <FormTextarea rows="8" value={draft.smsMessage}
                      onChange={e => this.onChange(e.target.value)}
                      placeholder={item.defaultSms}/>
                    <div style={{textAlign: 'right', fontSize: 11, color: smsLen > 90 ? '#D97706' : '#6B7280'}}>
                      {smsLen}자 / {smsType}
                    </div>
                  </Col>

                  <Col md="5">
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6}}>
                      <label style={{fontSize: 12, fontWeight: 600, margin: 0}}>
                        <FeatherIcon icon="eye" size="12" style={{verticalAlign: '-2px', marginRight: 4}}/>
                        미리보기 (샘플 데이터 적용)
                      </label>
                      <Button size="sm" theme="light" onClick={() => this.refreshPreview()} disabled={this.isPreviewLoading}>
                        <FeatherIcon icon="refresh-cw" size="11"/>
                      </Button>
                    </div>
                    <div style={{padding: 12, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 6, minHeight: 200}}>
                      <div style={{fontSize: 11, fontWeight: 700, color: '#1E40AF', marginBottom: 6}}>SMS</div>
                      <div style={{fontSize: 13, color: '#1E3A8A', whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, monospace'}}>
                        {this.preview.smsMessage || '-'}
                      </div>
                    </div>
                    <small style={{display: 'block', marginTop: 6, color: '#9CA3AF', fontSize: 11}}>
                      샘플: storeName=샘플 스토어, expiryDate=+7일, newPlan=프리미엄, prevPlan=베이직
                    </small>
                  </Col>
                </Row>

                <div style={{display: 'flex', justifyContent: 'flex-end', gap: 8, borderTop: '1px solid #E5E7EB', paddingTop: 12, marginTop: 14}}>
                  <Button theme="light" size="sm" onClick={() => this.close()} disabled={this.isSaving}>닫기</Button>
                  <Button theme="primary" size="sm" disabled={this.isSaving || !dirty} onClick={() => this.save()}>
                    <FeatherIcon icon="save" size="12" style={{verticalAlign: '-2px', marginRight: 4}}/>
                    {this.isSaving ? '저장 중…' : (dirty ? '저장' : '변경 없음')}
                  </Button>
                </div>
              </>
            )}
          </ModalBody>
        </Modal>
      </>
    );
  }
}

export default SmsTemplateModal;
